import { SelectionChangedEvent } from "ag-grid-community";
import { EMPTY_SEARCH_MODE_ENTITY } from "domain/entity/ChargeDataEnquiry/SearchModeEntity";
import { ChargeDetailEntity } from "domain/entity/ChargeDetailMaintenance/ChargeDetailEntity";
import { ChargeDetailConstant } from "presentation/constant/ChargeDetailMaintenance/ChargeDetailConstant";
import { CHARGE_DETAIL_AGENTDELIVERYDATE_COL_DEF } from "presentation/constant/ChargeDetailMaintenance/UpdateAgentDeliveryDateColumnDefinition";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { useChargeDetailVM } from "presentation/hook/ChargeDetailMaintenance/useChargeDetailVM";
import { useChargeHeaderMaintenanceVM } from "presentation/hook/ChargeHeaderMaintenance/useChargeHeaderMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { AgentDeliveryDateState } from "presentation/model/CounterCustomerCharge/CounterCustomerChargeMaintenanceModel";
import { useChargeDetailTracked } from "presentation/store/ChargeDetailMaintenance/ChargeDetailProvider";
import { useChargeHeaderMaintenanceTracked } from "presentation/store/ChargeHeaderMaintenance/ChargeHeaderMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { HPHButton, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const UpdateAgentDeliveryDatePanel = () => {
    const [chargeDetailState] = useChargeDetailTracked();
    const [chargeHeaderState] = useChargeHeaderMaintenanceTracked();
    const chargeDetailVM = useChargeDetailVM();
    const chargeHeaderVM = useChargeHeaderMaintenanceVM();
    const { agentDeliveryDateMap, selectedAgentDeliveryDateMap, chargeBatchUpdate } = chargeDetailState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();
    const gridRef: any = useRef(null);

    const vaildation = useCallback(() => {
        const currentDate = new Date();//dd-MM-yyyy HH:mm:ss
        currentDate.setHours(0, 0, 0, 0);
        let msg = null;
        let hasError = false;
        agentDeliveryDateMap.forEach((value) => {
            if (hasError) return;
            if (!value) {
                msg = "Please input the Agent delivery date time.";
                hasError = true;
                return;
            }
            const deliveryDate = new Date(value);
            if (deliveryDate < currentDate) {
                msg = "The Agent delivery date time should not be earlier than the current system date.";
                hasError = true;
                return;
            }
        });
        if (msg) {
            messageBarVM.showError(msg);
            return false;
        }

        return true;
    }, [agentDeliveryDateMap, messageBarVM]);

    const handleSaveClick = useCallback(async () => {
        if (!vaildation()) {
            return;
        }
        setIsLoading(true);
        // try {
        let newSearchCriteria = chargeDetailState.searchCriteria;
        newSearchCriteria = { ...newSearchCriteria, chargeHdrId: Number(chargeDetailState?.currentChargeHeader?.id ?? chargeHeaderState?.currentChargeHeader?.id) ?? 0 }

        let entityList: ChargeDetailEntity[] = [];
        Array.from(agentDeliveryDateMap).sort().map(([key, value]) => (
            chargeDetailState.chargeDetails?.filter(item => item.cntrNo === key && item.dtlState !== "DI" && item.dtlState !== "I" && item.adjType !== "Cancel Only")?.map(item => (
                entityList.push({
                    ...item,
                    agentDeliveryDate: value
                })
            ))
        ));

        chargeDetailVM.onSave(entityList).then((res) => {
            Promise.allSettled([
                chargeDetailVM.getChargeDetails(newSearchCriteria),
                chargeHeaderVM.onSearch({
                    ...chargeDetailState.searchCriteria,
                    id: chargeDetailState?.currentChargeHeader?.id ?? chargeHeaderState?.currentChargeHeader?.id
                }, EMPTY_SEARCH_MODE_ENTITY, true, true)
            ]).finally(() => {
                if (res && res["updateAgentDeliveryDateFail"]) {
                    const err: string = res["updateAgentDeliveryDateFail"]?.toString() ?? "";
                    messageBarVM.showError(err.replace("Error:", ""));
                } else {
                    messageBarVM.showSuccess(`Update ${MessageConstant.common.OPERATION_DONE_SUCCESSFUL.replace("{operationDone}", ChargeDetailConstant.Charge.AGENT_DELIVERY_DATE)}`);
                }
            });
        }).catch(() => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
            chargeDetailVM.onAgentDeliveryDateCloseClick();
        });

    }, [vaildation, agentDeliveryDateMap, chargeDetailVM, chargeDetailState.chargeDetails, chargeDetailState.searchCriteria, chargeDetailState?.currentChargeHeader?.id, chargeHeaderVM, chargeHeaderState?.currentChargeHeader?.id, messageBarVM]);

    const handClose = useCallback(() => {
        chargeDetailVM.onAgentDeliveryDateCloseClick();
    }, [chargeDetailVM]);

    const allRows = useMemo<AgentDeliveryDateState[]>(() => {
        if (!agentDeliveryDateMap || agentDeliveryDateMap.size === 0) {
            return [];
        }
        return Array.from(agentDeliveryDateMap)
            .map(([containerNo, agentDeliveryDate]) => ({
                cntrNo: containerNo,
                agentDeliveryDate: agentDeliveryDate,
            }))
            .sort((a, b) => a.cntrNo.localeCompare(b.cntrNo));
    }, [agentDeliveryDateMap]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        chargeDetailVM.updateSelectedAgentDeliveryDateRows(selectedRows);
    }, [chargeDetailVM])

    const handApply = useCallback(() => {
        if (!selectedAgentDeliveryDateMap || selectedAgentDeliveryDateMap.size === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        if (!chargeBatchUpdate.agentDeliveryDate) {
            messageBarVM.showError("Please input the Agent Delivery date time.");
            return;
        }
        chargeDetailVM.onApplyClick(agentDeliveryDateMap, selectedAgentDeliveryDateMap, "agentDeliveryDateMap");
    }, [selectedAgentDeliveryDateMap, chargeBatchUpdate.agentDeliveryDate, chargeDetailVM, agentDeliveryDateMap, messageBarVM]);


    const updateAgentDeliveryDatePanelTable =
        <NbisTable
            id='update-paid-through-date-table'
            isNewColumnSetting={false}
            columns={CHARGE_DETAIL_AGENTDELIVERYDATE_COL_DEF?.slice()}
            data={allRows ?? []}
            showPaginator={false}
            editable={false}
            showAddIcon={false}
            showUploadIcon={false}
            showDeleteButton={false}
            showReloadIcon={false}
            selectionMode={false}
            isRowHighligted={true}
            showPreferenceMenu={false}
            showSidebar={false}
            handleSelectionChanged={handleSelectionChange}
            gridHeight="customHeight"
            customHeight="calc(40vh)"
            ref={gridRef}
            rowSelection={"multiple"}
        />
    const memoAgentDeliveryDate = useMemo(() => {
        if (!agentDeliveryDateMap || agentDeliveryDateMap.size === 0) {
            return null;
        }

        return (
            <>
                <CriteriaItemContainer>
                    <div className="im-flex-row-item" style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
                        <DatePickerComponent
                            disabled={false}
                            label={ChargeDetailConstant.Charge.AGENT_DELIVERY_DATE}
                            width="220px"
                            date={chargeBatchUpdate?.agentDeliveryDate}
                            fieldName={"agentDeliveryDate"}
                            errorMessage=""
                            onDateChange={chargeDetailVM.onDatePickerChange}
                        />
                    </div>
                    <HPHButton label={'Apply'} size={'Small'} theme={'Secondary'} onClick={handApply} />
                </CriteriaItemContainer>
            </>
        );
    }, [agentDeliveryDateMap, chargeBatchUpdate?.agentDeliveryDate, chargeDetailVM.onDatePickerChange, handApply]);

    return (<div className='side-form-content-wrapper' style={{ height: 'calc(100vh - 8vh - 62px)' }}>
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <SidebarTitle>{"Update Agent Delivery Date"}</SidebarTitle>
                </Sidebarheader>
            </div>
        </div>

        {isLoading && <Loader Indicator="Spinner" size="Medium" />}

        <div style={{ marginTop: "-50px" }}>
            <TableWrapper>
                <div style={{ marginLeft: "15px" }}>{updateAgentDeliveryDatePanelTable}</div>
            </TableWrapper>
        </div>

        <div className={'add-edit-form'}>
            <div className="flex-row-auto">
                <div className={'flex-row-item'}>
                    {memoAgentDeliveryDate}
                </div>
            </div>
        </div>
        <SidebarActionBar>
            <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={handClose} />
            <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={() => handleSaveClick()} />
        </SidebarActionBar>
    </div>)
}

export default memo(UpdateAgentDeliveryDatePanel);